import React, { useState } from 'react';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Send, ChevronRight } from 'lucide-react';
import siteData from '@/data/siteData.json';

export const Products = ({ onRequestQuote }) => {
  const [activeCategory, setActiveCategory] = useState('Todos');

  const categories = ['Todos', ...siteData.categories.map((c) => c.shortName)];

  const filteredProducts =
    activeCategory === 'Todos'
      ? siteData.products
      : siteData.products.filter((p) => p.category === activeCategory);

  const handleRequestQuote = (product) => {
    if (onRequestQuote) {
      onRequestQuote(product);
    }
  };

  return (
    <section id="produtos" className="py-20 md:py-28 bg-background">
      <div className="section-container">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
            Catálogo
          </span>
          <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
            Nossos Produtos
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            Conheça nossa linha de produtos em automação industrial das melhores
            marcas do mercado
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex justify-center mb-12">
          <Tabs
            defaultValue="Todos"
            value={activeCategory}
            onValueChange={setActiveCategory}
            className="w-full max-w-2xl"
          >
            <TabsList className="grid w-full grid-cols-4 bg-secondary/50 p-1 rounded-lg">
              {categories.map((category) => (
                <TabsTrigger
                  key={category}
                  value={category}
                  className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-md text-sm font-medium transition-all"
                >
                  {category}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredProducts.map((product, index) => (
            <Card
              key={product.id}
              className="product-card flex flex-col group"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Product Image */}
              <div className="relative h-48 overflow-hidden bg-secondary">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-3 left-3">
                  <Badge
                    variant="secondary"
                    className="bg-background/90 text-foreground text-xs font-medium"
                  >
                    {product.brand}
                  </Badge>
                </div>
              </div>

              <CardContent className="flex-1 p-5">
                <Badge
                  variant="outline"
                  className="mb-3 text-xs border-border text-muted-foreground"
                >
                  {product.category}
                </Badge>
                <h3 className="font-display font-semibold text-foreground mb-2 line-clamp-2">
                  {product.name}
                </h3>
                <p className="text-muted-foreground text-sm mb-4 line-clamp-3">
                  {product.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-1.5">
                  {product.features.slice(0, 3).map((feature, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-xs text-muted-foreground"
                    >
                      {feature}
                    </span>
                  ))}
                  {product.features.length > 3 && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-secondary text-xs text-muted-foreground">
                      +{product.features.length - 3}
                    </span>
                  )}
                </div>
              </CardContent>

              <CardFooter className="p-5 pt-0 mt-auto">
                <Button
                  onClick={() => handleRequestQuote(product)}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 group/btn"
                >
                  <Send className="mr-2 h-4 w-4" />
                  Solicitar Orçamento
                  <ChevronRight className="ml-auto h-4 w-4 transition-transform group-hover/btn:translate-x-1" />
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {/* View More CTA */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Não encontrou o que procura? Entre em contato conosco.
          </p>
          <Button
            variant="outline"
            onClick={() => {
              const element = document.querySelector('#contato');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-outline-visible"
          >
            Fale Conosco
          </Button>
        </div>
      </div>
    </section>
  );
};
